import type { Event, EventParticipant, ParticipantRole } from '@casetrace/shared';

type AddressMetadata = { email?: unknown; name?: unknown };

const ROLE_LABELS: Record<ParticipantRole, string> = {
  from: 'Fra',
  to: 'Til',
  cc: 'Kopi',
  bcc: 'Blindkopi',
  group: 'Gruppe',
};

const ROLE_ORDER: ParticipantRole[] = ['from', 'to', 'cc', 'bcc', 'group'];

function isAddressMetadata(value: unknown): value is AddressMetadata {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function toText(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;
}

function parseAddress(role: ParticipantRole, value: unknown): EventParticipant | undefined {
  if (!isAddressMetadata(value)) {
    return undefined;
  }
  const email = toText(value.email);
  const name = toText(value.name);
  if (!email && !name) {
    return undefined;
  }
  return { role, email, name };
}

function parseAddressList(role: ParticipantRole, value: unknown): EventParticipant[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.flatMap((entry) => {
    const participant = parseAddress(role, entry);
    return participant ? [participant] : [];
  });
}

function fromMetadata(event: Event): EventParticipant[] {
  const fromAddress = parseAddress('from', event.metadata.from);
  const participants: EventParticipant[] = [
    ...parseAddressList('to', event.metadata.to),
    ...parseAddressList('cc', event.metadata.cc),
    ...parseAddressList('bcc', event.metadata.bcc),
  ];

  const senderHandle = toText(event.metadata.senderHandle);
  const recipientHandle = toText(event.metadata.recipientHandle);
  const senderName = toText(event.metadata.senderName);
  if (senderHandle) {
    participants.unshift({ role: 'from', handle: senderHandle });
  }
  if (recipientHandle) {
    participants.push({ role: 'to', handle: recipientHandle });
  }
  if (senderName && !senderHandle && !fromAddress) {
    participants.unshift({ role: 'from', name: senderName });
  }
  if (fromAddress) {
    participants.unshift(fromAddress);
  }
  return participants;
}

function displayValue(participant: EventParticipant): string {
  if (participant.name && participant.email) {
    return `${participant.name} <${participant.email}>`;
  }
  return participant.name ?? participant.email ?? participant.handle ?? 'Ukjent';
}

function readParticipants(event: Event): EventParticipant[] {
  if (event.participants && event.participants.length > 0) {
    return event.participants;
  }
  return fromMetadata(event);
}

export function EventParticipants({ event }: { event: Event }) {
  const participants = readParticipants(event);
  if (participants.length === 0) {
    return null;
  }

  const byRole = new Map<ParticipantRole, EventParticipant[]>();
  for (const participant of participants) {
    const existing = byRole.get(participant.role);
    if (existing) {
      existing.push(participant);
      continue;
    }
    byRole.set(participant.role, [participant]);
  }

  return (
    <div>
      {ROLE_ORDER.map((role) => {
        const entries = byRole.get(role) ?? [];
        if (entries.length === 0) {
          return null;
        }
        return (
          <div key={role} className="mb-2 last:mb-0">
            <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-text-tertiary">
              {ROLE_LABELS[role]}
            </p>
            <div className="flex flex-col gap-1">
              {entries.map((participant, index) => (
                <p
                  key={`${role}-${participant.email ?? participant.handle ?? participant.name ?? index}`}
                  className="truncate text-xs text-text-secondary"
                >
                  {displayValue(participant)}
                </p>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
